import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import API from '../utils/tenantApi';
import { useAuth } from '../context/AuthContext';

export default function TenantLoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { data } = await API.post('/tenant/login', form);
      login(data.token, { ...data.user, role: 'tenant' });
      navigate('/tenant/portal');
    } catch (err) {
      setError(err.response?.data?.error || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Brand accent */}
      <div className="h-0.5 bg-brand-600 w-full" />

      {/* Minimal nav */}
      <div className="bg-white border-b border-slate-200 px-6 h-14 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5">
          <div className="w-7 h-7 bg-brand-600 rounded-lg flex items-center justify-center shadow-sm">
            <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5}
                d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
            </svg>
          </div>
          <span className="font-bold text-slate-800 text-[15px] tracking-tight">
            Rent<span className="text-brand-600">Friendly</span>
          </span>
        </Link>
        <span className="text-xs bg-brand-50 text-brand-700 border border-brand-200 px-2.5 py-1 rounded-full font-medium">
          Tenant portal
        </span>
      </div>

      {/* Form */}
      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-sm">
          <div className="mb-7">
            <h1 className="text-2xl font-bold text-slate-900 tracking-tight">View your rent</h1>
            <p className="text-sm text-slate-500 mt-1">Sign in to see your payment balance</p>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-7">
            {error && (
              <div className="mb-5 flex items-start gap-2.5 p-3.5 bg-red-50 border border-red-200 rounded-lg">
                <svg className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Email address</label>
                <input
                  type="email"
                  value={form.email}
                  onChange={e => setForm({ ...form, email: e.target.value })}
                  required
                  autoComplete="email"
                  placeholder="your@email.com"
                  className="form-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Password</label>
                <input
                  type="password"
                  value={form.password}
                  onChange={e => setForm({ ...form, password: e.target.value })}
                  required
                  autoComplete="current-password"
                  placeholder="Your password"
                  className="form-input"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="btn-primary w-full justify-center mt-1"
              >
                {loading ? 'Signing in...' : 'Sign in'}
              </button>
            </form>

            <p className="text-center text-xs text-slate-400 mt-5 leading-relaxed">
              No account yet? Ask your landlord for an invite link.
            </p>
          </div>

          <div className="mt-5 space-y-2 text-center">
            <p className="text-sm text-slate-400">
              <Link to="/tenant/forgot-password" className="text-slate-500 hover:text-slate-700">Forgot your password?</Link>
            </p>
            <p className="text-sm text-slate-500">
              Are you a landlord?{' '}
              <Link to="/login" className="text-brand-600 hover:text-brand-700 font-medium">Sign in here</Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
